import type { IDraftByCategoryProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { useSite } from '@on3/ui-lib/index';
import { TeamAvatar } from '@public/components/Image/TeamAvatar';
import { TextLink } from '@public/components/UI/Link/TextLink';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import { Filter, makeList } from '../Filters/Filter';
import { FilterTeams } from '../Filters/FilterTeams';
import styles from './DraftByCategory.module.scss';
import { DraftByCategoryGroup } from './DraftByCategoryGroup';
import { DraftByCategoryNavigation } from './DraftByCategoryNavigation';

export const DraftByCategory = ({
  draftList,
  currentTeam,
  filters,
  title,
}: IDraftByCategoryProps) => {
  const { defaultYears } = useSite();
  const { nflDraftYear, nbaDraftYear } = defaultYears;
  const router = useRouter();
  const { year, 'team-key': teamKey, 'year-span': span } = router.query;
  const isNBA = router.asPath.includes('nba');
  const league = isNBA ? 'nba' : 'nfl';
  const basePath = router.asPath.split('?')[0];
  const leagueDraftYear = isNBA ? nbaDraftYear : nflDraftYear;
  const availableYears = filters.years?.filter(
    (yr) => yr <= leagueDraftYear && yr > 2011,
  );

  const getHref = (spanNum: number) => {
    if (spanNum === 1) {
      return `${basePath}?team-key=${teamKey}`;
    }

    return {
      pathname: router.pathname,
      query: { ...router.query, 'year-span': spanNum },
    };
  };

  const hasPlayers = draftList[0]?.players?.length;

  return (
    <section>
      <header className={styles.pageHeader}>
        {currentTeam?.siteSlug && (
          // TODO: we need the organization asset url
          <TeamAvatar
            className={styles.teamLogo}
            height={50}
            slug={currentTeam?.siteSlug}
            src={`https://on3static.com/teams/${currentTeam.siteSlug}.svg`}
            width={50}
          />
        )}
        <Text className={styles.pageTitle} variant="h1">
          {title}
        </Text>
      </header>
      <div className={styles.filterWrapper}>
        <div className={styles.spanLinkContainer}>
          {span && +span !== 1 && (
            <TextLink
              className={styles.spanLink}
              href={getHref(1)}
              underline="none"
              variant="button"
            >
              1 yr
            </TextLink>
          )}
          <TextLink
            className={clsx(styles.spanLink, {
              [styles.active]: span === '3',
            })}
            href={getHref(3)}
            underline="none"
            variant="button"
          >
            Last 3 Yrs
          </TextLink>

          <TextLink
            className={clsx(styles.spanLink, {
              [styles.active]: span === '5',
            })}
            href={getHref(5)}
            underline="none"
            variant="button"
          >
            Last 5 Yrs
          </TextLink>
        </div>
        <div className={styles.filterItemsContainer}>
          <div className={styles.filtersContainer}>
            <Filter
              list={makeList(availableYears)}
              name="year"
              selected={`${year}`}
            />
            <FilterTeams
              name="team-key"
              selected={`${teamKey}`}
              teams={filters.teams}
              useOrgKey
            />
          </div>
          <TextLink
            className={styles.draftLink}
            color="primary"
            href={`/${league}/draft/${year}/draft-history/team-draft-totals/`}
            underline="none"
            variant="subtitle1"
          >
            College Team Draft Totals
          </TextLink>
        </div>
      </div>
      <DraftByCategoryNavigation positions={filters.positions} />

      {!!hasPlayers &&
        draftList.map((group, index) => {
          return <DraftByCategoryGroup {...group} key={index} />;
        })}
      {!hasPlayers && (
        <Text className={styles.noDataText}>
          There is no data for the current selection.
        </Text>
      )}
    </section>
  );
};
